import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";

import Navbar from "./Navbar";
import Planning from "./Planning";
import Payroll from "./Payroll";
import Report from "./Report";
import api from "./api";

/** Aligné sur Prisma : WEEKLY = volume h/semaine, HOURLY = payé à l'heure */
const CONTRACT_BASE = { WEEKLY: "WEEKLY", HOURLY: "HOURLY" };
const DEFAULT_HOURLY_RATE = 25;
const DEFAULT_CONTRACT_H_PER_WEEK = 43.5;

function mapApiRoleToUi(role) {
  if (role === "MANAGER") return "manager";
  if (role === "EMPLOYEE") return "employee";
  return String(role || "").toLowerCase();
}

function normalizeSessionUser(u) {
  if (!u) return null;
  return {
    id: u.id,
    name: u.name,
    role: mapApiRoleToUi(u.role),
    pinResetRequired: u.pinResetRequired,
  };
}

function loginErrorMessage(err) {
  const code = err?.data?.error;
  if (err?.status === 423 || code === "account_locked") {
    return "Compte temporairement verrouillé. Réessaie plus tard.";
  }
  if (err?.status === 429 || code === "rate_limited") {
    return "Trop de tentatives. Réessaie dans une minute.";
  }
  if (code === "invalid_credentials") return "Identifiants incorrects.";
  const msg = String(err?.message || "");
  if (/fetch|network|Failed|Load failed/i.test(msg)) {
    return "Impossible de joindre le serveur. Vérifie que l'API répond et que tu es sur le bon domaine.";
  }
  return "Connexion impossible.";
}

function Login({ onLogin }) {
  const [mode, setMode] = useState("employee");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [publicNames, setPublicNames] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .listEmployeeNamesPublic()
      .then((names) => {
        if (!cancelled) setPublicNames(Array.isArray(names) ? names : []);
      })
      .catch(() => {
        if (!cancelled) setPublicNames([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const employeeChoices = publicNames;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    setLoading(true);
    try {
      if (mode === "employee") {
        if (!name) {
          setError("Choisis un employé.");
          setLoading(false);
          return;
        }
        const u = await api.loginEmployee(name, pin);
        onLogin(normalizeSessionUser(u));
      } else {
        const u = await api.loginManager(code);
        onLogin(normalizeSessionUser(u));
      }
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="login-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(to bottom right, #0f172a, #1e293b)",
      }}
    >
      <div
        className="login-box"
        style={{
          background: "white",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 20px 40px rgba(15,23,42,0.35)",
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: "4px" }}>Connexion</h2>
        <p style={{ marginTop: 0, marginBottom: "18px", color: "#64748b", fontSize: "0.9rem" }}>
          Choisis si tu te connectes en tant qu'employé ou manager.
        </p>

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "18px",
            background: "#e2e8f0",
            padding: "3px",
            borderRadius: "999px",
          }}
        >
          <button
            type="button"
            onClick={() => setMode("employee")}
            style={{
              flex: 1,
              border: "none",
              borderRadius: "999px",
              padding: "8px 0",
              cursor: "pointer",
              background: mode === "employee" ? "white" : "transparent",
              fontWeight: 600,
            }}
          >
            Employé
          </button>
          <button
            type="button"
            onClick={() => setMode("manager")}
            style={{
              flex: 1,
              border: "none",
              borderRadius: "999px",
              padding: "8px 0",
              cursor: "pointer",
              background: mode === "manager" ? "white" : "transparent",
              fontWeight: 600,
            }}
          >
            Manager
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {mode === "employee" ? (
            <>
              <label style={{ fontSize: "0.9rem", fontWeight: 500 }}>
                Nom de l'employé
                <select
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: "100%",
                    marginTop: "6px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5f5",
                  }}
                >
                  <option value="">Sélectionner...</option>
                  {employeeChoices.map((emp) => (
                    <option key={emp} value={emp}>
                      {emp}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: "0.9rem", fontWeight: 500 }}>
                Code personnel
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="ex: 4 chiffres"
                  style={{
                    width: "100%",
                    marginTop: "6px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5f5",
                  }}
                />
              </label>
            </>
          ) : (
            <label style={{ fontSize: "0.9rem", fontWeight: 500 }}>
              Code manager
              <input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: "6px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5f5",
                }}
              />
            </label>
          )}

          {error && (
            <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: "6px",
              background: loading ? "#94a3b8" : "#0f172a",
              color: "white",
              border: "none",
              borderRadius: "999px",
              padding: "10px 0",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AddEmployeeModal({ open, onClose, existingNames, onCreated, showToast }) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [hourlyRate, setHourlyRate] = useState(String(DEFAULT_HOURLY_RATE));
  const [contractBase, setContractBase] = useState(CONTRACT_BASE.WEEKLY);
  const [contractHours, setContractHours] = useState(String(DEFAULT_CONTRACT_H_PER_WEEK));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setPin("");
    setHourlyRate(String(DEFAULT_HOURLY_RATE));
    setContractBase(CONTRACT_BASE.WEEKLY);
    setContractHours(String(DEFAULT_CONTRACT_H_PER_WEEK));
    setError("");
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Indique un nom.");
      return;
    }
    if (existingNames.includes(trimmed)) {
      setError("Cet employé existe déjà.");
      return;
    }
    if (!pin.trim()) {
      showToast && showToast("Sans code PIN, l'employé ne pourra pas se connecter.");
    }
    const rateRaw = (hourlyRate || "").trim().replace(",", ".");
    const rateNum = Number(rateRaw);
    if (!Number.isFinite(rateNum) || rateNum <= 0) {
      setError("Indique un taux horaire valide (nombre > 0).");
      return;
    }
    let contractHoursPerWeek = null;
    if (contractBase === CONTRACT_BASE.WEEKLY) {
      const hRaw = (contractHours || "").trim().replace(",", ".");
      const hNum = hRaw === "" ? DEFAULT_CONTRACT_H_PER_WEEK : Number(hRaw);
      if (!Number.isFinite(hNum) || hNum <= 0) {
        setError("Indique un volume d'heures / semaine valide.");
        return;
      }
      contractHoursPerWeek = hNum;
    }
    try {
      await Promise.resolve(
        onCreated({
          name: trimmed,
          pin: pin.trim(),
          hourlyRate: rateNum,
          contractBase,
          contractHoursPerWeek,
        })
      );
      onClose();
    } catch {
      /* erreur serveur : toast côté parent, garder la modale ouverte */
    }
  }

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-employee-title"
        onClick={(ev) => ev.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 18px 35px rgba(15,23,42,0.18)",
          border: "1px solid #e2e8f0",
          padding: "18px 20px",
        }}
      >
        <h3 id="add-employee-title" style={{ margin: "0 0 14px", fontSize: "1.05rem", color: "#0f172a" }}>
          Ajouter un employé
        </h3>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Nom employé
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              autoFocus
              placeholder="Nom employé"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            PIN à transmettre
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoComplete="new-password"
              placeholder="PIN à transmettre"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Taux horaire
            <input
              type="text"
              inputMode="decimal"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="ex. 25"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Base
            <select
              value={contractBase}
              onChange={(e) => setContractBase(e.target.value)}
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            >
              <option value={CONTRACT_BASE.WEEKLY}>Base : heure / sem.</option>
              <option value={CONTRACT_BASE.HOURLY}>Base : payé à l&apos;heure</option>
            </select>
          </label>
          {contractBase === CONTRACT_BASE.WEEKLY ? (
            <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
              Heures / semaine (base contrat)
              <input
                type="text"
                inputMode="decimal"
                value={contractHours}
                onChange={(e) => setContractHours(e.target.value)}
                placeholder="ex. 43,5"
                style={{
                  width: "100%",
                  marginTop: "6px",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  border: "1px solid #cbd5f5",
                  fontSize: "0.95rem",
                  boxSizing: "border-box",
                }}
              />
            </label>
          ) : (
            <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b", lineHeight: 1.4 }}>
              Pas de volume contractuel hebdo : l&apos;indicatif mensuel paie utilisera les seules heures
              saisies dans le planning.
            </p>
          )}
          {error ? (
            <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>{error}</div>
          ) : null}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "6px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                borderRadius: "999px",
                border: "1px solid #cbd5f5",
                background: "white",
                color: "#475569",
                padding: "8px 16px",
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{
                borderRadius: "999px",
                border: "none",
                background: "#0f4dcc",
                color: "white",
                padding: "8px 18px",
                fontSize: "0.9rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Créer l&apos;employé
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetEmployeePinModal({ open, onClose, employees, onReset, showToast }) {
  const [employee, setEmployee] = useState("");
  const [newPin, setNewPin] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEmployee("");
    setNewPin("");
    setError("");
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!employee) {
      setError("Choisis un employé.");
      return;
    }
    const pin = newPin.trim();
    if (!pin) {
      setError("Indique un nouveau code PIN.");
      return;
    }
    try {
      await Promise.resolve(onReset(employee, pin));
      showToast && showToast(`Code mis à jour pour ${employee}.`);
      onClose();
    } catch (err) {
      setError(err?.message || "Échec de la mise à jour.");
    }
  }

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-pin-title"
        onClick={(ev) => ev.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 18px 35px rgba(15,23,42,0.18)",
          border: "1px solid #e2e8f0",
          padding: "18px 20px",
        }}
      >
        <h3 id="reset-pin-title" style={{ margin: "0 0 10px", fontSize: "1.05rem", color: "#0f172a" }}>
          Réinitialiser le code employé
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "#64748b", lineHeight: 1.45 }}>
          Définis un nouveau PIN — la mise à jour est enregistrée sur le serveur.
        </p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Employé
            <select
              value={employee}
              onChange={(e) => {
                setEmployee(e.target.value);
                if (error) setError("");
              }}
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            >
              <option value="">Choisir…</option>
              {employees.map((emp) => (
                <option key={emp} value={emp}>
                  {emp}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Nouveau PIN
            <input
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              placeholder="ex. 4 chiffres"
              autoComplete="new-password"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          {error ? <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>{error}</div> : null}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "6px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                borderRadius: "999px",
                border: "1px solid #cbd5f5",
                background: "white",
                color: "#475569",
                padding: "8px 16px",
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{
                borderRadius: "999px",
                border: "none",
                background: "#7c3aed",
                color: "white",
                padding: "8px 18px",
                fontSize: "0.9rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManagerPinResetBanner({ onOpenChangeCode }) {
  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "10px 16px",
        background: "linear-gradient(90deg, #fff7ed 0%, #ffedd5 100%)",
        borderBottom: "1px solid #fed7aa",
        color: "#9a3412",
        fontSize: "0.9rem",
        lineHeight: 1.45,
      }}
    >
      <span style={{ flex: "1 1 240px" }}>
        <strong>Sécurité :</strong> remplace le code de première connexion (bootstrap) par un code personnel. Tu peux
        retirer ensuite <code style={{ fontSize: "0.85em" }}>MANAGER_BOOTSTRAP_CODE</code> côté serveur.
      </span>
      <button
        type="button"
        onClick={onOpenChangeCode}
        style={{
          flexShrink: 0,
          borderRadius: "999px",
          border: "none",
          background: "#c2410c",
          color: "white",
          padding: "8px 16px",
          fontSize: "0.88rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Changer mon code
      </button>
    </div>
  );
}

function ChangeManagerCodeModal({ open, onClose, showToast, onCodeChanged }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
    setLoading(false);
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    const cur = current.trim();
    const neu = next.trim();
    if (!cur || !neu) {
      setError("Renseigne le code actuel et le nouveau code.");
      return;
    }
    if (neu.length < 4) {
      setError("Le nouveau code doit faire au moins 4 caractères.");
      return;
    }
    if (neu !== confirm.trim()) {
      setError("La confirmation ne correspond pas au nouveau code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.changePin(cur, neu);
      const u = await api.me();
      if (u && onCodeChanged) onCodeChanged(u);
      showToast && showToast("Code manager mis à jour.");
      onClose();
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-manager-code-title"
        onClick={(ev) => ev.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 18px 35px rgba(15,23,42,0.18)",
          border: "1px solid #e2e8f0",
          padding: "18px 20px",
        }}
      >
        <h3 id="change-manager-code-title" style={{ margin: "0 0 10px", fontSize: "1.05rem", color: "#0f172a" }}>
          Changer mon code manager
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "#64748b", lineHeight: 1.45 }}>
          Saisis ton code actuel puis le nouveau (minimum 4 caractères). Après enregistrement, utilise uniquement le
          nouveau code pour te connecter.
        </p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Code actuel
            <input
              type="password"
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value);
                if (error) setError("");
              }}
              autoComplete="current-password"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Nouveau code
            <input
              type="password"
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
                if (error) setError("");
              }}
              autoComplete="new-password"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#0f172a" }}>
            Confirmer le nouveau code
            <input
              type="password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (error) setError("");
              }}
              autoComplete="new-password"
              style={{
                width: "100%",
                marginTop: "6px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: "1px solid #cbd5f5",
                fontSize: "0.95rem",
                boxSizing: "border-box",
              }}
            />
          </label>
          {error ? <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>{error}</div> : null}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "6px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                borderRadius: "999px",
                border: "1px solid #cbd5f5",
                background: "white",
                color: "#475569",
                padding: "8px 16px",
                fontSize: "0.9rem",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                borderRadius: "999px",
                border: "none",
                background: loading ? "#94a3b8" : "#0f172a",
                color: "white",
                padding: "8px 18px",
                fontSize: "0.9rem",
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Toast({ message, visible, onDone }) {
  useEffect(() => {
    if (!visible || !message) return;
    const t = setTimeout(() => onDone(), 2500);
    return () => clearTimeout(t);
  }, [visible, message, onDone]);
  if (!visible || !message) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        background: "#0f172a",
        color: "white",
        padding: "12px 20px",
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        zIndex: 9999,
        fontSize: "0.95rem",
        fontWeight: 500,
      }}
    >
      {message}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null); // { id?, role, name, pinResetRequired? }
  const [employees, setEmployees] = useState(() => []);
  const [employeeContractHours, setEmployeeContractHours] = useState(() => ({}));
  const [employeeHourlyRate, setEmployeeHourlyRate] = useState(() => ({}));
  const [employeeContractBase, setEmployeeContractBase] = useState(() => ({}));
  const [employeeIdByName, setEmployeeIdByName] = useState({});
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);
  const [resetPinOpen, setResetPinOpen] = useState(false);
  const [changeManagerCodeOpen, setChangeManagerCodeOpen] = useState(false);
  const [hasPendingWeeks, setHasPendingWeeks] = useState(false);
  const [toast, setToast] = useState({ message: "", visible: false });
  const [apiSessionReady, setApiSessionReady] = useState(false);

  const showToast = useCallback((message) => {
    setToast({ message, visible: true });
  }, []);
  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, visible: false }));
  }, []);

  const refreshEmployeesFromApi = useCallback(async () => {
    const list = await api.listEmployees();
    const ids = {};
    const hours = {};
    const rates = {};
    const bases = {};
    for (const e of list) {
      ids[e.name] = e.id;
      hours[e.name] = e.contractHoursPerWeek ?? 0;
      rates[e.name] = e.hourlyRate ?? DEFAULT_HOURLY_RATE;
      bases[e.name] = e.contractBase === "HOURLY" ? CONTRACT_BASE.HOURLY : CONTRACT_BASE.WEEKLY;
    }
    setEmployeeIdByName(ids);
    setEmployees(list.map((x) => x.name));
    setEmployeeContractHours(hours);
    setEmployeeHourlyRate(rates);
    setEmployeeContractBase(bases);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await api.me();
        if (cancelled) return;
        if (u) setUser(normalizeSessionUser(u));
      } catch (_) {
        /* pas de session */
      } finally {
        if (!cancelled) setApiSessionReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshEmployeesFromApi().catch((err) => {
      showToast(loginErrorMessage(err));
    });
  }, [user, refreshEmployeesFromApi, showToast]);

  useEffect(() => {
    if (user?.role !== "manager") {
      setHasPendingWeeks(false);
      return undefined;
    }
    let cancelled = false;
    const refreshPending = async () => {
      try {
        const d = await api.getPendingSubmissions();
        if (!cancelled) setHasPendingWeeks(!!d.hasPending);
      } catch {
        if (!cancelled) setHasPendingWeeks(false);
      }
    };
    refreshPending();
    const id = setInterval(refreshPending, 45_000);
    window.addEventListener("focus", refreshPending);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", refreshPending);
    };
  }, [user?.role, user?.id]);

  const handleLogout = useCallback(async () => {
    try {
      await api.logout();
    } catch (_) {}
    setEmployeeIdByName({});
    setEmployees([]);
    setEmployeeContractHours({});
    setEmployeeHourlyRate({});
    setEmployeeContractBase({});
    setUser(null);
  }, []);

  if (!user) {
    if (!apiSessionReady) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#64748b",
            fontSize: "0.95rem",
          }}
        >
          Chargement de la session…
        </div>
      );
    }
    return <Login onLogin={setUser} />;
  }

  return (
    <BrowserRouter>
      <Toast message={toast.message} visible={toast.visible} onDone={hideToast} />
      <ChangeManagerCodeModal
        open={changeManagerCodeOpen}
        onClose={() => setChangeManagerCodeOpen(false)}
        showToast={showToast}
        onCodeChanged={(u) => {
          if (u) setUser(normalizeSessionUser(u));
        }}
      />
      <ResetEmployeePinModal
        open={resetPinOpen}
        onClose={() => setResetPinOpen(false)}
        employees={employees}
        showToast={showToast}
        onReset={async (name, newPin) => {
          const id = employeeIdByName[name];
          if (!id) throw new Error("Employé introuvable.");
          try {
            await api.resetEmployeePin(id, newPin);
          } catch (err) {
            throw new Error(loginErrorMessage(err));
          }
        }}
      />
      <AddEmployeeModal
        open={addEmployeeOpen}
        onClose={() => setAddEmployeeOpen(false)}
        existingNames={employees}
        showToast={showToast}
        onCreated={async ({ name, pin, hourlyRate, contractBase, contractHoursPerWeek }) => {
          if (!pin || String(pin).trim().length < 4) {
            showToast("Le PIN doit faire au moins 4 caractères (exigence du serveur).");
            throw new Error("pin_invalid");
          }
          try {
            await api.createEmployee(
              name.trim(),
              pin.trim(),
              typeof hourlyRate === "number" && hourlyRate > 0 ? hourlyRate : DEFAULT_HOURLY_RATE,
              contractBase === CONTRACT_BASE.HOURLY ? CONTRACT_BASE.HOURLY : CONTRACT_BASE.WEEKLY,
              contractBase === CONTRACT_BASE.WEEKLY && contractHoursPerWeek != null && Number.isFinite(contractHoursPerWeek)
                ? contractHoursPerWeek
                : contractBase === CONTRACT_BASE.HOURLY
                ? 0
                : DEFAULT_CONTRACT_H_PER_WEEK
            );
            await refreshEmployeesFromApi();
            showToast(`« ${name} » a été ajouté.`);
          } catch (err) {
            const code = err?.data?.error;
            if (code === "name_taken") showToast("Ce nom est déjà utilisé.");
            else showToast(loginErrorMessage(err));
            throw err;
          }
        }}
      />
      <Navbar
        user={user}
        hasPendingWeeks={hasPendingWeeks}
        onLogout={handleLogout}
        onChangeManagerCode={user?.role === "manager" ? () => setChangeManagerCodeOpen(true) : undefined}
      />
      {user?.role === "manager" && user?.pinResetRequired ? (
        <ManagerPinResetBanner onOpenChangeCode={() => setChangeManagerCodeOpen(true)} />
      ) : null}

      <Routes>
        <Route
          path="/"
          element={
            <Planning
              user={user}
              employees={employees}
              employeeIdByName={employeeIdByName}
              employeeContractHours={employeeContractHours}
              employeeHourlyRate={employeeHourlyRate}
              employeeContractBase={employeeContractBase}
              onOpenAddEmployee={() => setAddEmployeeOpen(true)}
              onOpenResetEmployeePin={() => setResetPinOpen(true)}
              onRemoveEmployee={async (name) => {
                const id = employeeIdByName[name];
                if (!id) return;
                try {
                  await api.deactivateEmployee(id);
                  await refreshEmployeesFromApi();
                  showToast(`${name} a été désactivé.`);
                } catch (err) {
                  showToast(loginErrorMessage(err));
                }
              }}
              showToast={showToast}
            />
          }
        />
        <Route path="/payroll" element={<Payroll user={user} employees={employees} showToast={showToast} />} />
        <Route path="/rapport" element={<Report user={user} employees={employees} showToast={showToast} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
